<template>
  <div class="returns-page py-3">
    <header class="returns-toolbar">
      <div class="returns-toolbar__title">
        <span class="returns-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Mark Returns</h1>
          <p>Scan an order number to mark it as returned</p>
        </div>
      </div>
    </header>

    <CAlert v-if="toast.message" :color="toast.kind" show class="returns-alert">
      {{ toast.message }}
    </CAlert>

    <section class="scan-zone">
      <div class="scan-zone__head">
        <h2>Scan zone</h2>
        <span class="scan-zone__hint">Scan an order number to load a return preview</span>
      </div>
      <div class="return-scan">
        <div :class="['scan-field', 'is-active']">
          <div class="scan-field__step">Order Number</div>
          <input
            id="scan-return-order"
            ref="returnOrderInput"
            v-model="returnOrderInputValue"
            type="text"
            class="scan-input"
            autocomplete="off"
            spellcheck="false"
            inputmode="text"
            placeholder="Scan or type order number"
            :disabled="returnIsConfirming"
            @keydown.enter.prevent="onReturnOrderEnter"
          >
          <div v-if="returnIsLookingUp" class="scan-field__hint-text">Looking up order…</div>
          <div v-else-if="returnLookupError" class="scan-zone__error" role="alert" style="margin-top: 0.5rem;">
            {{ returnLookupError }}
          </div>
        </div>
      </div>

      <div v-if="returnPreview" class="return-preview">
        <div class="return-preview__head">
          <span class="return-preview__label">Confirm return</span>
          <button type="button" class="scan-field__cancel" @click="cancelReturnPreview" :disabled="returnIsConfirming">Cancel</button>
        </div>
        <div class="return-preview__order">
          <span class="return-preview__order-num">{{ returnPreview.order_number }}</span>
          <span v-if="returnPreview.buyer_name" class="return-preview__buyer">{{ returnPreview.buyer_name }}</span>
        </div>
        <div class="return-preview__grid">
          <div class="return-preview__field">
            <span class="return-preview__field-label">Order Status</span>
            <span :class="['status-pill', statusPillClass(returnPreview.order_status)]">
              {{ returnPreview.order_status || 'unknown' }}
            </span>
          </div>
          <div class="return-preview__field">
            <span class="return-preview__field-label">Shipping Status</span>
            <span :class="['status-pill', statusPillClass(returnPreview.shipping_status)]">
              {{ returnPreview.shipping_status || 'unknown' }}
            </span>
          </div>
          <div v-if="returnPreview.shipping_method" class="return-preview__field">
            <span class="return-preview__field-label">Shipping Method</span>
            <span class="ship-tag">{{ formatShippingMethod(returnPreview.shipping_method) }}</span>
          </div>
          <div v-if="returnPreview.tracking_id" class="return-preview__field">
            <span class="return-preview__field-label">Tracking ID</span>
            <span class="cell-mono">{{ returnPreview.tracking_id }}</span>
          </div>
          <div v-if="returnPreview.contact_phone" class="return-preview__field">
            <span class="return-preview__field-label">Phone</span>
            <span class="cell-mono">{{ returnPreview.contact_phone }}</span>
          </div>
          <div v-if="returnPreview.grand_total != null" class="return-preview__field">
            <span class="return-preview__field-label">Total</span>
            <span class="cell-mono">{{ returnPreview.grand_total }}</span>
          </div>
        </div>
        <div v-if="returnPreview.order_status === 'returned' || returnPreview.shipping_status === 'returned'" class="return-preview__warning">
          This order is already marked as returned.
        </div>
        <div class="return-preview__actions">
          <button
            type="button"
            class="ghost-btn"
            :disabled="returnIsConfirming"
            @click="cancelReturnPreview"
          >Cancel</button>
          <button
            type="button"
            class="danger-btn"
            :disabled="returnIsConfirming"
            @click="confirmReturn"
          >
            <span v-if="returnIsConfirming" class="submit-btn__spinner" aria-hidden="true"></span>
            <span>{{ returnIsConfirming ? 'Marking…' : 'Confirm Return' }}</span>
          </button>
        </div>
      </div>
    </section>

    <section class="rows-card">
      <div class="rows-card__head">
        <h2>Marked as returned</h2>
        <span v-if="returnedRows.length" class="rows-card__count">{{ returnedRows.length }}</span>
      </div>

      <div v-if="!returnedRows.length" class="rows-card__empty">
        <div class="empty-glyph" aria-hidden="true">↩</div>
        <p>No orders marked as returned yet. Scan an order number above.</p>
      </div>

      <div v-else class="rows-card__scroll">
        <table class="rows-table">
          <thead>
            <tr>
              <th class="col-num">#</th>
              <th>Order Number</th>
              <th>Marked At</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in returnedRows" :key="row.uid">
              <td class="col-num">{{ returnedRows.length - index }}</td>
              <td class="cell-order">
                <span class="cell-mono">{{ row.order_number }}</span>
                <span v-if="row.buyer_name" class="cell-buyer">{{ row.buyer_name }}</span>
              </td>
              <td><span class="cell-time">{{ formatTime(row.marked_at) }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

const STORAGE_KEY = 'scannerReturns:v1'

function generateUid () {
  return `r-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function trimScanValue (value) {
  if (value === null || value === undefined) return ''
  return String(value).replace(/[\r\n\t]/g, '').trim()
}

export default {
  name: 'ScannerReturns',
  data () {
    return {
      returnOrderInputValue: '',
      returnPreview: null,
      returnIsLookingUp: false,
      returnIsConfirming: false,
      returnLookupSeq: 0,
      returnLookupError: '',
      returnedRows: [],
      toast: { message: '', kind: 'success' },
      toastTimer: null
    }
  },
  watch: {
    returnedRows: {
      deep: true,
      handler () {
        this.persistState()
      }
    }
  },
  created () {
    this.restoreState()
  },
  mounted () {
    this.$nextTick(() => this.focusReturnOrderInput())
  },
  beforeDestroy () {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
    }
  },
  methods: {
    focusReturnOrderInput () {
      const el = this.$refs.returnOrderInput
      if (el && typeof el.focus === 'function') {
        el.focus()
        el.select && el.select()
      }
    },
    validateFormat (value, label) {
      if (value.length < 3) {
        return `${label} is too short.`
      }
      if (value.length > 64) {
        return `${label} is too long.`
      }
      if (!/^[\w\-./|:]+$/.test(value)) {
        return `${label} contains unsupported characters.`
      }
      return ''
    },
    statusPillClass (status) {
      if (!status) return 'status-pill--neutral'
      const s = String(status).toLowerCase()
      if (s === 'returned' || s === 'cancelled') return 'status-pill--danger'
      if (s === 'shipped' || s === 'delivered') return 'status-pill--success'
      if (s === 'ready_to_ship' || s === 'processing') return 'status-pill--warn'
      return 'status-pill--neutral'
    },
    async onReturnOrderEnter () {
      const value = trimScanValue(this.returnOrderInputValue)
      this.returnLookupError = ''
      if (!value) {
        this.returnOrderInputValue = ''
        this.focusReturnOrderInput()
        return
      }

      const formatError = this.validateFormat(value, 'Order number')
      if (formatError) {
        this.returnLookupError = formatError
        this.returnOrderInputValue = ''
        this.focusReturnOrderInput()
        return
      }

      const seq = ++this.returnLookupSeq
      this.returnIsLookingUp = true
      this.returnPreview = null

      try {
        const response = await axios.post(`${this.$apiAdress}/api/orders/scanner_lookup_order`, { order_number: value })
        if (seq !== this.returnLookupSeq) return
        const data = response && response.data ? response.data : {}
        if (!data.found) {
          this.returnLookupError = `Order "${value}" not found.`
          return
        }
        this.returnPreview = {
          order_number: String(data.order_number || value),
          buyer_name: data.buyer_name ? String(data.buyer_name) : '',
          contact_phone: data.contact_phone ? String(data.contact_phone) : '',
          shipping_method: data.shipping_method ? String(data.shipping_method) : '',
          shipping_status: data.shipping_status ? String(data.shipping_status) : '',
          order_status: data.order_status ? String(data.order_status) : '',
          tracking_id: data.tracking_id ? String(data.tracking_id) : '',
          grand_total: data.grand_total != null ? data.grand_total : null
        }
        this.returnOrderInputValue = ''
      } catch (e) {
        if (seq !== this.returnLookupSeq) return
        const msg = (e && e.response && e.response.data && e.response.data.message) || 'Lookup failed.'
        this.returnLookupError = msg
      } finally {
        if (seq === this.returnLookupSeq) this.returnIsLookingUp = false
      }
    },
    cancelReturnPreview () {
      this.returnPreview = null
      this.returnOrderInputValue = ''
      this.returnLookupError = ''
      this.$nextTick(() => this.focusReturnOrderInput())
    },
    async confirmReturn () {
      if (!this.returnPreview || this.returnIsConfirming) return
      const orderNumber = this.returnPreview.order_number
      this.returnIsConfirming = true
      try {
        const response = await axios.post(`${this.$apiAdress}/api/orders/scanner_mark_returned`, { order_number: orderNumber })
        const data = response && response.data ? response.data : {}
        if (!data.success) {
          this.showToast('danger', data.message || 'Failed to mark returned.')
          return
        }
        this.returnedRows = [
          {
            uid: generateUid(),
            order_number: orderNumber,
            buyer_name: this.returnPreview.buyer_name,
            marked_at: new Date().toISOString()
          },
          ...this.returnedRows
        ]
        this.showToast('success', `Order ${orderNumber} marked as returned.`)
        this.returnPreview = null
        this.returnOrderInputValue = ''
        this.$nextTick(() => this.focusReturnOrderInput())
      } catch (error) {
        const message = (error && error.response && error.response.data && error.response.data.message) ||
          'Network error, please retry.'
        this.showToast('danger', message)
      } finally {
        this.returnIsConfirming = false
      }
    },
    formatShippingMethod (slug) {
      if (!slug) return ''
      return String(slug)
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    },
    formatTime (iso) {
      if (!iso) return '—'
      const date = new Date(iso)
      if (Number.isNaN(date.getTime())) return '—'
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },
    showToast (kind, message, ttl = 4500) {
      this.toast = { kind, message }
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.toast = { message: '', kind: 'success' }
      }, ttl)
    },
    persistState () {
      try {
        const snapshot = { returnedRows: this.returnedRows }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot))
      } catch (e) {
        // localStorage unavailable; ignore.
      }
    },
    restoreState () {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (parsed && Array.isArray(parsed.returnedRows)) {
          this.returnedRows = parsed.returnedRows
            .filter((row) => row && row.order_number)
            .map((row) => ({
              uid: row.uid || generateUid(),
              order_number: String(row.order_number),
              buyer_name: row.buyer_name || '',
              marked_at: row.marked_at || new Date().toISOString()
            }))
        }
      } catch (e) {
        // Corrupt storage; ignore.
      }
    }
  }
}
</script>

<style scoped>
.returns-page {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.returns-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 0.5rem;
}

.returns-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.returns-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.returns-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.returns-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.returns-alert {
  margin: 0;
  border-radius: 10px;
}

.ghost-btn {
  height: 38px;
  padding: 0 0.9rem;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.ghost-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.ghost-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.danger-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 38px;
  padding: 0 0.9rem;
  border: 1px solid #dc2626;
  border-radius: 8px;
  background: #dc2626;
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}

.danger-btn:hover:not(:disabled) {
  background: #b91c1c;
  box-shadow: 0 2px 6px rgba(220, 38, 38, 0.25);
}

.danger-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.submit-btn__spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  animation: spin 0.65s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Scan zone */
.scan-zone {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  padding: 1rem 1.1rem 1.1rem;
}

.scan-zone__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
}

.scan-zone__head h2 {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
}

.scan-zone__hint {
  font-size: 0.75rem;
  color: #6b7280;
}

.return-scan {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  max-width: 480px;
}

.scan-field {
  position: relative;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.75rem 0.85rem 0.85rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.scan-field.is-active {
  background: #f5fbf8;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.scan-field__step {
  display: inline-block;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #16a07a;
  margin-bottom: 0.25rem;
}

.scan-input {
  width: 100%;
  height: 44px;
  padding: 0 0.85rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  color: #1f2a44;
  letter-spacing: 0.01em;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.scan-input::placeholder {
  color: #9aa3b2;
  font-weight: 500;
  letter-spacing: 0.01em;
  font-family: inherit;
}

.scan-input:focus {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.scan-input:disabled {
  background: #f1f5f9;
  cursor: not-allowed;
  color: #94a3b8;
}

.scan-field__cancel {
  border: 0;
  background: transparent;
  color: #b45309;
  font-weight: 600;
  font-size: 0.75rem;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
}

.scan-field__hint-text {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #94a3b8;
}

.scan-zone__error {
  margin-top: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: #fff1f2;
  color: #b91c1c;
  border: 1px solid #fecdd3;
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Return preview */
.return-preview {
  margin-top: 1rem;
  border: 1px solid #fecdd3;
  background: #fff7f7;
  border-radius: 10px;
  padding: 0.9rem 1rem 1rem;
}

.return-preview__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.6rem;
}

.return-preview__label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #b91c1c;
}

.return-preview__order {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
}

.return-preview__order-num {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
}

.return-preview__buyer {
  font-size: 0.85rem;
  font-weight: 500;
  color: #475569;
}

.return-preview__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.7rem 1rem;
  margin-bottom: 0.85rem;
}

.return-preview__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.return-preview__field-label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}

.return-preview__warning {
  margin: 0 0 0.85rem;
  padding: 0.45rem 0.7rem;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  border-radius: 8px;
  color: #b91c1c;
  font-size: 0.78rem;
  font-weight: 600;
}

.return-preview__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.32rem 0.7rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  font-size: 0.78rem;
  color: #475569;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.status-pill--success {
  border-color: #c8ead9;
  background: #f5fbf8;
  color: #0e5b46;
}

.status-pill--warn {
  border-color: #fde68a;
  background: #fffbeb;
  color: #92400e;
}

.status-pill--neutral {
  border-color: #e5e9f2;
  background: #f8fafc;
  color: #475569;
}

.status-pill--danger {
  border-color: #fecdd3;
  background: #fff1f2;
  color: #b91c1c;
}

.ship-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.6rem;
  background: #f5fbf8;
  border: 1px solid #c8ead9;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 600;
  color: #0e5b46;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

/* Rows table card */
.rows-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.rows-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.rows-card__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.rows-card__count {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #475569;
  background: #f1f5f9;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
}

.rows-card__empty {
  padding: 2.2rem 1rem;
  text-align: center;
  color: #64748b;
}

.empty-glyph {
  font-size: 2rem;
  color: #cbd5e1;
  margin-bottom: 0.4rem;
}

.rows-card__empty p {
  margin: 0;
  font-size: 0.88rem;
}

.rows-card__scroll {
  overflow-x: auto;
  max-height: 60vh;
}

.rows-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}

.rows-table thead th {
  background: #f8fafc;
  color: #475569;
  font-weight: 700;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.65rem 1rem;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 1;
  white-space: nowrap;
}

.rows-table tbody td {
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  color: #1f2937;
  vertical-align: middle;
}

.rows-table tbody tr:last-child td {
  border-bottom: none;
}

.col-num {
  width: 56px;
  font-variant-numeric: tabular-nums;
  color: #64748b;
}

.cell-mono {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-weight: 600;
  color: #1f2a44;
}

.cell-time {
  color: #64748b;
  font-variant-numeric: tabular-nums;
}

.cell-order {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.cell-buyer {
  font-size: 0.74rem;
  font-weight: 500;
  color: #475569;
  letter-spacing: 0;
}

/* Mobile */
@media (max-width: 720px) {
  .scan-input {
    height: 48px;
    font-size: 1.05rem;
  }

  .rows-table thead th,
  .rows-table tbody td {
    padding: 0.55rem 0.7rem;
  }

  .rows-card__scroll {
    max-height: 50vh;
  }
}
</style>
